import { Route, Routes } from "react-router-dom";
import { Fragment, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { GoogleOAuthProvider } from "@react-oauth/google";
import { ThemeProvider } from "./contexts/ThemeContext";
import { Toaster } from "sonner";
import useDisableHoverOnScroll from "./hooks/useDisableHoverOnScroll.js";
import { refresh } from "./store/slices/auth/asyncThunks.js";
import { fetchUserInfo } from "./store/slices/user/asyncThunk.js";
import {
  selectLoggedIn,
  setLoggedIn,
  setAuthLoading,
} from "./store/slices/auth/slice.js";
import VerifyEmailPage from "./screens/Verify/VerifyEmail.js";
import TermsPage from "./screens/TermsPage/TermsPage.js";
import PrivacyPage from "./screens/PrivacyPage/PrivacyPage.js";
import DeleteAccountPage from "./screens/DeleteAccountPage/DeleteAccountPage.js";
import HomePage from "./screens/HomePage/HomePage.js";
import SubscriptionPage from "./screens/SubscriptionPage/SubscriptionPage.js";
import RewardsTemplatePage from "./screens/RewardsTemplatePage/RewardsTemplatePage.js";
import TrainingPage from "./screens/TrainingPage/TrainingPage.js";
import TrainTutorialPage from "./screens/TrainTutorialPage/TrainTutorialPage.js";
import ReferralEarningsPage from "./screens/ReferralEarningsPage/ReferralEarningsPage.js";
import CollectionPage from "./screens/CollectionPage/CollectionPage.js";
import TrainPage from "./screens/TrainPage/TrainPage.js";
import TrainingCompletedPage from "./screens/TrainingCompletedPage/TrainingCompletedPage.js";
import GlobalStyles from "./styles/globalStyles";
import "./styles/index.css";
import { lineSpinner } from "ldrs";
import "ldrs/react/Spiral.css";
import ProtectedRoute from "./components/utils/ProtectedRoute.js";
import { getCategories } from "./store/slices/model/asyncThunks.js";
import TrainingSelectCoverPage from "./screens/TrainingSelectCoverPage/TrainingSelectCoverPage.js";
import TelegramTutorialPage from "./screens/TutorialsPages/TelegramTutorialPage.js";
import CryptoTutorialPage from "./screens/TutorialsPages/CryptoTutorialPage.js";
import WalletPage from "./screens/WalletPage/WalletPage.js";
import { getOrCreateBrowserId } from "./utils/utils.js";
import { selectUser } from "./store/slices/user/slice.js";
import { VIDEO_RESOLUTION } from "./utils/constants.js";

lineSpinner.register();

// Protected Route Component

const AppContent = () => {
  const dispatch = useDispatch();
  const loggedIn = useSelector(selectLoggedIn);
  const user = useSelector(selectUser);
  useDisableHoverOnScroll();

  useEffect(() => {
    dispatch(getCategories());
  }, [dispatch]);

  useEffect(() => {
    getOrCreateBrowserId();
    if (loggedIn === null) {
      dispatch(refresh())
        .unwrap()
        .then(() => {
          dispatch(fetchUserInfo());
        })
        .catch(() => {
          dispatch(setLoggedIn({ loggedIn: false }));
        })
        .finally(() => {
          dispatch(setAuthLoading(false));
        });
    }
  }, [dispatch, loggedIn]);

  useEffect(() => {
    if (loggedIn) {
      if (
        user?.subscription &&
        user?.subscription?.name &&
        user?.subscription?.endDate &&
        new Date(user?.subscription?.endDate) > new Date()
      ) {
        const resolution = localStorage.getItem("videoResolution");
        const fps = localStorage.getItem("videoFps");
        const settingsAlreadyReset = localStorage.getItem(
          "settingsAlreadyReset"
        );

        if (!settingsAlreadyReset) {
          if (!resolution || resolution === VIDEO_RESOLUTION.LOW) {
            localStorage.setItem("videoResolution", VIDEO_RESOLUTION.MEDIUM);
          }

          if (!fps || parseInt(fps) === 30) {
            localStorage.setItem("videoFps", 45);
          }
          localStorage.setItem("settingsAlreadyReset", true);
        }
      }
    }
  }, [loggedIn, user]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return undefined;
    }

    const handleWheel = (event) => {
      if (event.ctrlKey) {
        event.preventDefault();
      }
    };

    const handleKeyDown = (event) => {
      const zoomKeys = ["=", "+", "-", "_"];
      if ((event.ctrlKey || event.metaKey) && zoomKeys.includes(event.key)) {
        event.preventDefault();
      }
      if ((event.ctrlKey || event.metaKey) && event.key === "0") {
        event.preventDefault();
      }
    };

    const handleTouchMove = (event) => {
      if (event.scale && event.scale !== 1) {
        event.preventDefault();
      }
      if (event.touches && event.touches.length > 1) {
        event.preventDefault();
      }
    };

    let lastTouchEnd = 0;
    const handleTouchEnd = (event) => {
      const now = Date.now();
      if (now - lastTouchEnd <= 300) {
        event.preventDefault();
      }
      lastTouchEnd = now;
    };

    const preventGesture = (event) => {
      event.preventDefault();
    };

    document.addEventListener("wheel", handleWheel, { passive: false });
    document.addEventListener("keydown", handleKeyDown);
    document.addEventListener("touchmove", handleTouchMove, { passive: false });
    document.addEventListener("touchend", handleTouchEnd, { passive: false });
    window.addEventListener("gesturestart", preventGesture, { passive: false });
    window.addEventListener("gesturechange", preventGesture, {
      passive: false,
    });
    window.addEventListener("gestureend", preventGesture, { passive: false });

    return () => {
      document.removeEventListener("wheel", handleWheel);
      document.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("touchmove", handleTouchMove);
      document.removeEventListener("touchend", handleTouchEnd);
      window.removeEventListener("gesturestart", preventGesture);
      window.removeEventListener("gesturechange", preventGesture);
      window.removeEventListener("gestureend", preventGesture);
    };
  }, []);

  // useEffect(() => { ???
  //   const params = new URLSearchParams(location.search);
  //   const ref = params.get ("ref");

  //   if (!loggedIn) {
  //     if (ref) {
  //       dispatch(setAffiliateRef(ref));
  //       localStorage.setItem("affiliateRef", ref);

  //       dispatch(setSignUpModalOpen(true));
  //     } else {
  //       const savedRef = localStorage.getItem("affiliateRef");
  //       if (savedRef) {
  //         dispatch(setAffiliateRef(savedRef));
  //       } else {
  //         dispatch(setSignUpModalOpen(false));
  //         dispatch(setAffiliateRef(null));
  //       }
  //     }
  //   } else {
  //     if (ref) {
  //       params.delete("ref");
  //       navigate({ search: params.toString() }, { replace: true });

  //       dispatch(setAffiliateRef(null));
  //       localStorage.removeItem("affiliateRef");
  //     }
  //   }
  // }, [dispatch, location.pathname, location.search, loggedIn, navigate]);

  return (
    <Fragment>
      <GlobalStyles />

      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/collection" element={<CollectionPage />} />
        <Route path="/train" element={<TrainPage />} />
        <Route path="/subscription" element={<SubscriptionPage />} />
        <Route path="/verify-email/:tokenId" element={<VerifyEmailPage />} />
        <Route path="/account/delete" element={<DeleteAccountPage />} />
        <Route path="/terms-and-conditions" element={<TermsPage />} />
        <Route path="/privacy-policy" element={<PrivacyPage />} />

        <Route path="/telegram-tutorial" element={<TelegramTutorialPage />} />
        <Route path="/crypto-tutorial" element={<CryptoTutorialPage />} />

        {/* Protected Routes */}
        <Route path="/wallet" element={<WalletPage />} />
        <Route
          path="/rewards/template"
          element={
            <ProtectedRoute>
              <RewardsTemplatePage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/referral/earnings"
          element={
            <ProtectedRoute>
              <ReferralEarningsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/train/:modelId"
          element={
            <ProtectedRoute>
              <TrainingPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/train/select-cover/:modelId"
          element={<TrainingSelectCoverPage />}
        />
        <Route
          path="/train/completed/:modelId"
          element={
            <ProtectedRoute>
              <TrainingCompletedPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/train-tutorial"
          element={
            <ProtectedRoute>
              <TrainTutorialPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/account/delete"
          element={
            <ProtectedRoute>
              <DeleteAccountPage />
            </ProtectedRoute>
          }
        />
      </Routes>

      <Toaster
        position="bottom-right"
        closeButton={false}
        richColors
        toastOptions={{
          style: {
            borderRadius: 24,
            border: "1px solid #18181b",
            background: "#0a0b0c",
            color: "white",
          },
        }}
      />
    </Fragment>
  );
};

function App() {
  return (
    <ThemeProvider>
      <GoogleOAuthProvider clientId="72791619053-28tkj855p9smue8nao7puaqmqoct7lcn.apps.googleusercontent.com">
        <AppContent />
      </GoogleOAuthProvider>
    </ThemeProvider>
  );
}

export default App;
